// Bulk customer import — paste from a spreadsheet or pick a CSV.
//
// The preview is the point of this dialog. An import is hard to undo, so
// nothing is written until the operator has seen, row by row, what will be
// created, what will be skipped as already-known, and what cannot be read.

import { useMemo, useRef, useState } from 'react';
import { Download, Upload, ClipboardPaste, AlertTriangle, Check, SkipForward } from 'lucide-react';
import { CUSTOMER_TEMPLATE, downloadTemplate } from '@/lib/importTemplates';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { Chip } from '@/components/ui/Chip';
import { parseCustomerRows, type ParsedRow } from '@/lib/importParse';
import { normalizePhoneKey, type ImportOutcome } from '@/lib/api/customers';
import { useCustomers, useImportCustomers } from '@/hooks/useCustomers';
import { formatNumber } from '@/lib/format';
import { IMPORT_ACCEPT, spreadsheetFileToText } from '@/lib/spreadsheet';

export interface CustomerImportDialogProps {
  open: boolean;
  onClose: () => void;
}

type RowStatus = 'baru' | 'sudah-ada' | 'invalid';

interface PreviewRow extends ParsedRow {
  status: RowStatus;
  reason?: string;
}

export function CustomerImportDialog({ open, onClose }: CustomerImportDialogProps) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<ImportOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: existing } = useCustomers();
  const importMut = useImportCustomers();

  const parsed = useMemo(() => parseCustomerRows(text), [text]);

  // Existing phones, in comparison form. This mirrors the check the API does;
  // it is here only so the operator sees the outcome before committing, not as
  // the guarantee — the API re-checks against the full table.
  const knownPhones = useMemo(
    () => new Set((existing ?? []).map((c) => normalizePhoneKey(c.phone))),
    [existing],
  );

  const preview: PreviewRow[] = useMemo(() => {
    const seenInFile = new Set<string>();
    return parsed.rows.map((r) => {
      const key = normalizePhoneKey(r.phone);
      if (!r.name.trim()) return { ...r, status: 'invalid', reason: 'Nama kosong' };
      if (!key) return { ...r, status: 'invalid', reason: 'Nomor telepon kosong / tidak valid' };
      if (knownPhones.has(key)) return { ...r, status: 'sudah-ada' };
      if (seenInFile.has(key)) return { ...r, status: 'sudah-ada', reason: 'Duplikat di dalam file ini' };
      seenInFile.add(key);
      return { ...r, status: 'baru' };
    });
  }, [parsed.rows, knownPhones]);

  const counts = useMemo(() => ({
    baru: preview.filter((r) => r.status === 'baru').length,
    ada: preview.filter((r) => r.status === 'sudah-ada').length,
    invalid: preview.filter((r) => r.status === 'invalid').length,
  }), [preview]);

  const reset = () => {
    setText(''); setFileName(null); setResult(null); setError(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    try {
      setText(await spreadsheetFileToText(f));
      setFileName(f.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'File tidak bisa dibaca.');
    }
  };

  const handleImport = async () => {
    setError(null);
    const rows = preview
      .filter((r) => r.status === 'baru')
      .map(({ name, taxId, phone, address }) => ({ name, taxId, phone, address }));
    if (rows.length === 0) return;
    try {
      setResult(await importMut.mutateAsync(rows));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impor gagal');
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Import customer"
      description="Tempel dari Excel, atau pilih file .xlsx / CSV. Urutan kolom: Nama, NIK/NPWP, Nomor Telepon, Alamat."
      size="xl"
      footer={
        result ? (
          <Button variant="primary" onClick={() => { reset(); onClose(); }}>Selesai</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => { reset(); onClose(); }}>Batal</Button>
            <Button
              variant="primary"
              onClick={handleImport}
              loading={importMut.isPending}
              disabled={counts.baru === 0}
            >
              {counts.baru > 0 ? `Import ${formatNumber(counts.baru)} customer` : 'Import'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3">
          <div className="p-4 rounded border border-success/30 bg-success/5">
            <p className="text-body font-medium text-ink">
              {formatNumber(result.imported)} customer ditambahkan.
            </p>
            {result.skippedExisting > 0 && (
              <p className="text-small text-ink-subtle mt-1">
                {formatNumber(result.skippedExisting)} dilewati karena nomornya sudah terdaftar —
                data lama tidak diubah.
              </p>
            )}
          </div>
          {result.failed.length > 0 && (
            <div className="p-4 rounded border border-danger/30 bg-danger/5">
              <p className="text-small font-medium text-danger mb-2">
                {formatNumber(result.failed.length)} baris gagal:
              </p>
              <ul className="text-caption text-ink-subtle space-y-1 max-h-40 overflow-y-auto">
                {result.failed.map((f, i) => (
                  <li key={i}>{f.row.name || '(tanpa nama)'} — {f.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept={IMPORT_ACCEPT}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button variant="secondary" leftIcon={<Upload size={16} />} onClick={() => fileRef.current?.click()}>
              Pilih file Excel / CSV
            </Button>
            {/* The column order is otherwise stated only in prose below, so the
                first thing anyone does is guess. The sample rows demonstrate
                the parts that are easy to get wrong. */}
            <Button
              variant="ghost"
              leftIcon={<Download size={16} />}
              onClick={() => {
                setError(null);
                void downloadTemplate(CUSTOMER_TEMPLATE).catch((e) =>
                  setError(e instanceof Error ? e.message : 'Gagal membuat template'),
                );
              }}
            >
              Download template Excel
            </Button>
            {fileName && <Chip variant="default">{fileName}</Chip>}
            {text && (
              <Button variant="ghost" onClick={reset}>Bersihkan</Button>
            )}
          </div>

          <div>
            <label htmlFor="import-paste" className="block text-small font-medium text-ink mb-1.5">
              <ClipboardPaste size={14} className="inline mr-1.5 -mt-0.5" />
              Atau tempel langsung dari Excel
            </label>
            <textarea
              id="import-paste"
              value={text}
              onChange={(e) => { setText(e.target.value); setFileName(null); }}
              rows={5}
              spellCheck={false}
              className="w-full rounded border border-line bg-surface px-3 py-2 text-small font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder={'Budi Santoso\t3201234567890001\t081234567890\tJl. Merdeka No. 5, Jakarta'}
            />
            <p className="mt-1.5 text-caption text-ink-subtle">
              Satu baris per customer. Baris judul kolom akan dilewati otomatis.
            </p>
          </div>

          {error && (
            <div role="alert" className="p-3 rounded border border-danger/30 bg-danger/10 text-small text-danger">
              {error}
            </div>
          )}

          {preview.length > 0 && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Chip variant="success">
                  <Check size={12} className="inline mr-1" />
                  {formatNumber(counts.baru)} akan ditambahkan
                </Chip>
                {counts.ada > 0 && (
                  <Chip variant="default">
                    <SkipForward size={12} className="inline mr-1" />
                    {formatNumber(counts.ada)} sudah ada — dilewati
                  </Chip>
                )}
                {counts.invalid > 0 && (
                  <Chip variant="danger">
                    <AlertTriangle size={12} className="inline mr-1" />
                    {formatNumber(counts.invalid)} tidak valid
                  </Chip>
                )}
                {parsed.headerSkipped && (
                  <span className="text-caption text-ink-subtle">Baris judul dilewati.</span>
                )}
              </div>

              <div className="max-h-72 overflow-auto border border-line rounded">
                <Table>
                  <THead>
                    <TR>
                      <TH>Status</TH>
                      <TH>Nama</TH>
                      <TH>NIK / NPWP</TH>
                      <TH>Telepon</TH>
                      <TH>Alamat</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {preview.map((r) => (
                      <TR key={r.line}>
                        <TD>
                          {r.status === 'baru' && <Chip variant="success">Baru</Chip>}
                          {r.status === 'sudah-ada' && <Chip variant="default">Sudah ada</Chip>}
                          {r.status === 'invalid' && (
                            <span className="text-caption text-danger">{r.reason}</span>
                          )}
                        </TD>
                        <TD>{r.name || <span className="text-ink-subtle">—</span>}</TD>
                        <TD className="font-mono text-caption">{r.taxId || '—'}</TD>
                        <TD className="font-mono text-caption">{r.phone || '—'}</TD>
                        <TD className="max-w-[220px] truncate" title={r.address}>{r.address || '—'}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            </>
          )}

          {parsed.problems.length > 0 && (
            <div className="p-3 rounded border border-warning/30 bg-warning/5 text-caption text-ink-subtle">
              {formatNumber(parsed.problems.length)} baris dilewati karena tidak terbaca (baris{' '}
              {parsed.problems.slice(0, 5).map((p) => p.line).join(', ')}
              {parsed.problems.length > 5 ? '…' : ''}).
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
